import { ArrowLeftRight, ChevronDown } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  CountBadge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Skeleton,
  Typography,
} from "@facade/components/ui";
import { Link, useNavigate, useParams } from "react-router-dom";
import { use, useState } from "react";

import { Project } from "@facade/contracts";
import type { ProjectTab } from "../../pages/ViewProjectPage/ViewProjectPage.types";
import { validTabs } from "../../pages/ViewProjectPage/ViewProjectPage.types";
import { SideBarContext } from "../../contexts";
import { cn } from "@facade/lib/utils";
import { getInitials } from "@facade/utils";
import { projectsQueries } from "../../queries";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const ProjectImage = ({ project }: { project: Project }) => {
  const [loadingImage, setLoadingImage] = useState(true);

  return project.imageUrl ? (
    <>
      {loadingImage ? <Skeleton className="absolute h-full w-full" /> : null}
      <img
        src={project.imageUrl}
        className="h-full w-full shrink-0 rounded-md object-fill"
        onLoad={() => setLoadingImage(false)}
        onError={() => setLoadingImage(false)}
      />
    </>
  ) : (
    <Typography
      variant="text-xs"
      className="relative grid h-full w-full place-items-center font-semibold text-white drop-shadow-md transition-all duration-250 group-hover:scale-105"
    >
      {getInitials(project.name)}
    </Typography>
  );
};

export const ProjectAppBarStartContent = () => {
  const navigate = useNavigate();
  const { projectId, tab } = useParams();
  const { t } = useTranslation();
  const { open: isSidebarOpen } = use(SideBarContext);

  if (!projectId) {
    throw new Error();
  }

  const { data: project } = useSuspenseQuery(projectsQueries.detail(projectId));
  const { data: projects } = useSuspenseQuery(projectsQueries.list());

  const handleProjectClick = (nextProjectId: string) => {
    const destinationTab = validTabs.includes(tab as ProjectTab)
      ? (tab as ProjectTab)
      : "issues";

    navigate(`/projects/${nextProjectId}/${destinationTab}`);
  };

  return (
    <span
      data-open={isSidebarOpen}
      className={cn(
        "flex items-center gap-2.5",
        "[--project-sidebar-width:62px]",
        "data-[open=true]:[--project-sidebar-width:240px]",
        "pl-(--project-sidebar-width)",
        "transition-[padding-left] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]",
        "will-change-[padding-left]",
      )}
    >
      <span className="grid grid-cols-[1fr_auto] border rounded-md">
        <div className="flex items-center gap-3 px-2.5 py-0.5">
          <div className="relative shrink-0 h-8 w-8 rounded-md bg-accent">
            <ProjectImage project={project} />
          </div>

          <div className="flex flex-col justify-center">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">{t("projects.title")}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>

                <BreadcrumbSeparator />
              </BreadcrumbList>
            </Breadcrumb>

            <Typography
              variant="text-xs"
              className="text-accent-foreground font-semibold"
            >
              {project.name}
            </Typography>
          </div>
        </div>

        <div className="flex items-center border-l transition-all hover:bg-accent/10 rounded-r-md">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="p-2">
                <ChevronDown className="size-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-auto max-w-72">
              <DropdownMenuLabel className="flex gap-2">
                <ArrowLeftRight className="size-4" />
                Troca rápida
              </DropdownMenuLabel>
              {projects.map((x) => (
                <DropdownMenuItem
                  key={x.id}
                  onClick={() => handleProjectClick(x.id)}
                >
                  <div className="relative shrink-0 h-8 w-8 rounded-md bg-accent-higher">
                    <ProjectImage project={x} />
                  </div>
                  <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                    {x.name}
                  </span>
                  <CountBadge
                    count={x.unreadNotificationCount ?? 0}
                    className="ml-auto shrink-0"
                    aria-label={`${x.unreadNotificationCount ?? 0} unread notifications`}
                  />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </span>
    </span>
  );
};
